import { ArrowRight, Calendar, Filter, RefreshCw, Search, Users } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export const PayrollFiltersToolbar = ({
  monthLabel,
  periodYear,
  totalRows,
  activeFiltersCount,
  sortLabel,
  filteredSummary,
  searchQuery,
  onSearchChange,
  employeeFilter,
  onEmployeeFilterChange,
  employees = [],
  periodMonth,
  onPeriodMonthChange,
  onPeriodYearChange,
  yearOptions = [],
  monthOptions = [],
  statusFilter,
  onStatusFilterChange,
  payrollSortField,
  onPayrollSortFieldChange,
  payrollSortDirection,
  onPayrollSortDirectionChange,
  payrollSortOptions = [],
  onClearFilters,
}) => (
  <Card className="overflow-hidden rounded-xl border-slate-200 shadow-sm" data-testid="payroll-toolbar-card">
    <CardContent className="space-y-2 p-2.5">
      <div className="flex flex-wrap items-center gap-1.5 text-xs" data-testid="payroll-kpi-strip">
        <Badge variant="outline" className="rounded-full bg-white">{monthLabel} {periodYear}</Badge>
        <Badge variant="outline" className="rounded-full bg-white">{totalRows} folhas</Badge>
        {activeFiltersCount > 0 && <Badge variant="outline" className="rounded-full bg-amber-50 text-amber-700">{activeFiltersCount} filtro(s)</Badge>}
        <Badge variant="outline" className="rounded-full bg-white">Sort: {sortLabel}</Badge>
        <Badge variant="outline" className="rounded-full bg-white">Bruto: {formatCurrency(filteredSummary.totalGross)}</Badge>
        <Badge variant="outline" className="rounded-full bg-blue-50 text-blue-700">INSS: {formatCurrency(filteredSummary.totalInss)}</Badge>
        <Badge variant="outline" className="rounded-full bg-purple-50 text-purple-700">IRPS: {formatCurrency(filteredSummary.totalIrps)}</Badge>
        <Badge variant="outline" className="rounded-full bg-emerald-50 text-emerald-700">Líquido: {formatCurrency(filteredSummary.totalNet)}</Badge>
      </div>

      <div className="flex flex-col gap-1.5 rounded-xl border border-slate-200 bg-white/90 p-2 shadow-sm" data-testid="payroll-compact-controls">
        <div className="flex flex-wrap items-center gap-1.5">
          <div className="relative min-w-[220px] flex-[1.1]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Pesquisar número ou funcionário..."
              value={searchQuery}
              onChange={(event) => onSearchChange(event.target.value)}
              className="h-8 rounded-lg border-slate-200 bg-slate-50 pl-9 text-sm"
              data-testid="search-input"
            />
          </div>

          <Select value={employeeFilter} onValueChange={onEmployeeFilterChange}>
            <SelectTrigger className="h-8 w-[176px] rounded-lg border-slate-200 bg-slate-50 text-sm" data-testid="payroll-employee-filter">
              <Users className="mr-2 h-4 w-4 text-slate-400" />
              <SelectValue placeholder="Funcionário" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos funcionários</SelectItem>
              {employees.map((employee) => (
                <SelectItem key={employee.id} value={employee.id}>{employee.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={String(periodMonth)} onValueChange={onPeriodMonthChange}>
            <SelectTrigger className="h-8 w-[126px] rounded-lg border-slate-200 bg-slate-50 text-sm" data-testid="payroll-month-filter">
              <Calendar className="mr-2 h-4 w-4 text-slate-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map((month) => (
                <SelectItem key={month.value} value={String(month.value)}>{month.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={String(periodYear)} onValueChange={onPeriodYearChange}>
            <SelectTrigger className="h-8 w-[90px] rounded-lg border-slate-200 bg-slate-50 text-sm" data-testid="payroll-year-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map((year) => (
                <SelectItem key={year} value={String(year)}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={onStatusFilterChange}>
            <SelectTrigger className="h-8 w-[124px] rounded-lg border-slate-200 bg-slate-50 text-sm" data-testid="status-filter">
              <Filter className="mr-2 h-4 w-4 text-slate-400" />
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="draft">Gerado</SelectItem>
              <SelectItem value="confirmed">Revisto</SelectItem>
              <SelectItem value="paid">Processado</SelectItem>
            </SelectContent>
          </Select>

          <Select value={payrollSortField} onValueChange={onPayrollSortFieldChange}>
            <SelectTrigger className="h-8 w-[134px] rounded-lg border-slate-200 bg-slate-50 text-sm" data-testid="payroll-sort-field">
              <ArrowRight className="mr-2 h-4 w-4 text-slate-400" />
              <SelectValue placeholder="Ordenar" />
            </SelectTrigger>
            <SelectContent>
              {payrollSortOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={payrollSortDirection} onValueChange={onPayrollSortDirectionChange}>
            <SelectTrigger className="h-8 w-[96px] rounded-lg border-slate-200 bg-slate-50 text-sm" data-testid="payroll-sort-direction">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="asc">Asc.</SelectItem>
              <SelectItem value="desc">Desc.</SelectItem>
            </SelectContent>
          </Select>

          {activeFiltersCount > 0 && (
            <Button variant="ghost" size="sm" onClick={onClearFilters} className="h-8 rounded-lg border border-slate-200 bg-slate-50 px-2 text-slate-700 hover:bg-slate-100" data-testid="payroll-clear-filters-button">
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);